import { Link } from 'react-router-dom';
import useCmsPage from '../hooks/useCmsPage';
import useSeoMeta from '../hooks/useSeoMeta';
import './Bailleurs.css';

// ── Contenu par défaut ─────────────────────────────────────────────────
const D = {
  hero: {
    title: "Partenaires & Bailleurs de fonds",
    subtitle: "Ensemble, finançons la formation des talents tech africains. Rejoignez l'écosystème TTA.",
    cta_label: "Devenir partenaire",
    cta_href: "/contact",
  },
  stats: [
    { value: '500+', label: 'Talents formés' },
    { value: '85%', label: "Taux d'insertion" },
    { value: '5', label: 'Programmes' },
    { value: '2+', label: 'Pays couverts' },
  ],
  textImage: {
    title: "Pourquoi financer la formation tech\u00a0?",
    paragraphs: [
      "Le déficit de talents numériques en Afrique de l'Ouest représente un frein majeur à la transformation digitale des économies de la région. En finançant la formation, vous investissez directement dans le capital humain qui permettra à ces économies de prendre leur place dans l'économie numérique mondiale.",
      "Avec TTA, votre investissement se traduit par des résultats concrets : 85% d'insertion professionnelle dans les 3 mois, des profils formés aux standards internationaux, et un impact mesurable sur les parcours individuels.",
    ],
  },
  cards: [
    { title: 'Financement de cohortes', text: "Sponsorisez une cohorte entière ou partielle. Vous choisissez le programme, le nombre de bénéficiaires et les critères de sélection." },
    { title: 'Bourses individuelles', text: "Financez des bourses nominatives pour des candidats présélectionnés selon vos critères (genre, région, profil)." },
    { title: 'Partenariat entreprise', text: "Formez vos propres collaborateurs ou accédez en priorité aux profils formés par TTA pour vos recrutements." },
  ],
  cta: {
    title: "Intéressé par un partenariat\u00a0?",
    text: "Notre équipe est disponible pour discuter des modalités et construire un programme adapté à vos objectifs.",
    cta_label: "Nous contacter",
    cta_href: "/contact",
  },
};

const VTL_STEPS = [
  { num: '01', title: 'Prise de contact', text: "Échangez avec notre équipe pour définir vos objectifs et le type de partenariat souhaité." },
  { num: '02', title: 'Conception du programme', text: "Nous co-construisons un programme sur mesure adapté à vos critères et votre budget." },
  { num: '03', title: 'Sélection des bénéficiaires', text: "Sélection rigoureuse des candidats selon vos critères avec notre processus de recrutement éprouvé." },
  { num: '04', title: 'Formation & suivi', text: "Vos bénéficiaires rejoignent un bootcamp intensif. Reporting régulier sur leur progression." },
];

const CARD_ICONS = [
  <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
  <><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></>,
  <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
];

function parseParagraphs(html) {
  if (!html) return [];
  return html.replace(/<p>/gi, '').split(/<\/p>/i).map(p => p.replace(/<[^>]+>/g, '').trim()).filter(Boolean);
}

export default function Bailleurs() {
  const { sections, page } = useCmsPage('bailleurs');
  useSeoMeta(page, { title: 'Bailleurs & Partenaires — TTA', desc: "Financez la formation des talents tech africains. Rejoignez l'écosystème TTA." });

  const get = (type) => sections.find(s => s.type === type)?.content;

  const hero       = get('hero')       || D.hero;
  const textImage  = get('text_image') || {};
  const cardsData  = get('cards')      || {};
  const statsData  = get('stats')      || {};
  const ctaData    = get('cta')        || D.cta;

  const statItems   = statsData.items  || D.stats;
  const cardItems   = cardsData.items  || D.cards;
  const pourquoiTitle = textImage.title || D.textImage.title;
  const pourquoiParas = parseParagraphs(textImage.text);
  const finalParas  = pourquoiParas.length > 0 ? pourquoiParas : D.textImage.paragraphs;

  return (
    <main>

      {/* ── HERO SOMBRE ── */}
      <section className="bailleurs-hero">
        <div className="bailleurs-hero__bg" />
        <div className="bailleurs-hero__inner">
          <div>
            <div className="section-eyebrow" style={{ color: 'rgba(139,197,63,.85)', borderColor: 'rgba(139,197,63,.2)', marginBottom: '16px' }}>
              Partenaires &amp; Financement
            </div>
            <h1 className="bailleurs-hero__title">{hero.title}</h1>
            <p className="bailleurs-hero__sub">{hero.subtitle}</p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {hero.cta_label && (
                <Link to={hero.cta_href || '/contact'} className="btn-primary">{hero.cta_label} →</Link>
              )}
              {hero.cta2_label && (
                <Link to={hero.cta2_href || '/formations'} className="btn-outline" style={{ color: 'rgba(255,255,255,.7)', borderColor: 'rgba(255,255,255,.2)' }}>
                  {hero.cta2_label}
                </Link>
              )}
            </div>
          </div>
          <div className="bailleurs-hero__right">
            {statItems.map((stat, i) => (
              <div key={i} className="bailleurs-hero__stat">
                <span className="grad-text" style={{ fontFamily: 'var(--fs)', fontSize: '32px', fontWeight: 800, letterSpacing: '-1px' }}>{stat.value}</span>
                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,.5)', marginTop: '4px' }}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── POURQUOI FINANCER / IMPACT ── */}
      <section id="impact" style={{ padding: '80px 40px', background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }}>
            <div>
              <div className="section-eyebrow">Investir dans le capital humain</div>
              <h2 className="section-title" style={{ fontSize: '34px', marginBottom: '20px' }}>{pourquoiTitle}</h2>
              {finalParas.map((para, i) => (
                <p key={i} style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '14px' }}>{para}</p>
              ))}
            </div>
            <div style={{ background: 'var(--section-alt-bg)', borderRadius: 'var(--radius-xl)', padding: '32px', border: '0.5px solid var(--border-color)' }}>
              {[
                { label: "Taux d'insertion", value: '85%' },
                { label: 'Alumni en emploi', value: '500+' },
                { label: 'Salaire moyen post-formation', value: '×2.5' },
                { label: 'Entreprises partenaires', value: '15+' },
              ].map((item, i, arr) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '16px 0', borderBottom: i < arr.length - 1 ? '0.5px solid var(--border-light)' : 'none',
                }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>{item.label}</span>
                  <span className="grad-text" style={{ fontFamily: 'var(--fs)', fontWeight: 800, fontSize: '24px' }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── AVANTAGES ── */}
      <section style={{ padding: '80px 40px', background: 'var(--section-alt-bg)' }}>
        <div className="container">
          <div className="section-eyebrow" style={{ marginBottom: '8px' }}>Comment nous accompagner</div>
          <h2 className="section-title" style={{ marginBottom: '40px' }}>{cardsData.title || 'Comment nous accompagner\u00a0?'}</h2>
          <div className="bailleurs-avantages">
            {cardItems.map((item, i) => (
              <div key={i} className="avantage-card">
                <div className="avantage-card__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {CARD_ICONS[i % CARD_ICONS.length]}
                  </svg>
                </div>
                <div className="avantage-card__title">{item.title}</div>
                <div className="avantage-card__desc">{item.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESSUS VTL ── */}
      <section style={{ padding: '80px 40px', background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="section-eyebrow" style={{ marginBottom: '8px' }}>Comment ça se passe</div>
          <h2 className="section-title" style={{ marginBottom: '4px' }}>Le processus de partenariat</h2>
          <p className="section-subtitle" style={{ marginBottom: 0 }}>Simple, transparent, efficace.</p>
          <div className="vtl">
            {VTL_STEPS.map((step, i) => {
              const isLeft = i % 2 === 0;
              const card = (
                <div className="vtl__card">
                  <div className="vtl__card-inner">
                    <div className="vtl__card-step">Étape {step.num}</div>
                    <div className="vtl__card-title">{step.title}</div>
                    <div className="vtl__card-desc">{step.text}</div>
                  </div>
                </div>
              );
              const node = (
                <div className="vtl__node">
                  <div className="vtl__node-circle">
                    <div className="vtl__node-num">{step.num}</div>
                    <div className="vtl__node-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  </div>
                </div>
              );
              return (
                <div key={i} className={`vtl__row vtl__row--${isLeft ? 'left' : 'right'}`}>
                  {isLeft ? <>{card}{node}<div className="vtl__empty" /></> : <><div className="vtl__empty" />{node}{card}</>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA SOMBRE ── */}
      <section style={{
        padding: '80px 40px', textAlign: 'center',
        background: 'linear-gradient(135deg, #0a1a0e 0%, #0d1f14 50%, #152e1c 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-100px', left: '50%', transform: 'translateX(-50%)',
          width: '500px', height: '500px',
          background: 'radial-gradient(circle, rgba(139,197,63,.1) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontFamily: 'var(--fs)', fontSize: '36px', fontWeight: 800, color: '#fff', marginBottom: '16px' }}>
            {ctaData.title}
          </h2>
          {ctaData.text && (
            <p style={{ color: 'rgba(255,255,255,.65)', fontSize: '16px', maxWidth: '520px', margin: '0 auto 32px' }}>
              {ctaData.text}
            </p>
          )}
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {ctaData.cta_label && <Link to={ctaData.cta_href || '/contact'} className="btn-primary">{ctaData.cta_label} →</Link>}
            {ctaData.cta2_label && (
              <Link to={ctaData.cta2_href || '/formations'} className="btn-outline" style={{ color: 'rgba(255,255,255,.8)', borderColor: 'rgba(255,255,255,.2)' }}>
                {ctaData.cta2_label}
              </Link>
            )}
          </div>
        </div>
      </section>

    </main>
  );
}
